<template>
  <SidebarAuditLog
    v-if="open.content === RightDrawerContent.AuditLog && shouldRenderAuditLog"
  />
  <!-- We use v-show because the communication component needs to mount under all circumstances as Audit Log weaves in communication items between Audit Logs -->
  <SfSidepaneCommunication
    v-if="!onWorkflowReview"
    v-show="open.content === RightDrawerContent.Communication"
    :ledger-label="ledgerLabel"
    :owner-id="ownerId"
    :owner-type="ownerType"
  />
  <WorkflowReviewSidepane
    v-if="onWorkflowReview"
    v-show="open.content === RightDrawerContent.Communication"
    :ledger-id="ledgerId"
    :ledger-label="ledgerLabel"
  />
</template>

<script setup lang="ts">
import { useParentElement } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { watchEffect } from 'vue'

import { SilverfinContext } from '@app/shared/types/sf-data'
import SidebarAuditLog from '@app/application/sidebar-audit-log/sidebar-audit-log.vue'
import WorkflowReviewSidepane from '@app/application/workflows/screens/workflow-review/components/workflow-review-sidepane.vue'
import SfSidepaneCommunication from '@app/shared/vue/components/layout/sf-sidepane.vue'
import { RightDrawerContent } from '@app/shared/types/drawer'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useContext } from '@app/shared/pinia/context'
import { useDrawer } from '@app/shared/pinia/drawer'

defineOptions({
  name: 'SfSidepaneContentSwitcher',
})

interface BaseProps {
  ledgerLabel?: string
  shouldRenderAuditLog?: boolean
}

interface SfSidepaneProps extends BaseProps {
  contextId?: number
  contextType?: SilverfinContext.Account | SilverfinContext.ReconciliationText
  onWorkflowReview?: false
  ownerId?: number
  ownerType?: string
}

interface WorkflowReviewSidepaneProps extends BaseProps {
  ledgerId?: string
  onWorkflowReview?: true
}

type Props = SfSidepaneProps | WorkflowReviewSidepaneProps

const parent = useParentElement()

const props = defineProps<Props>()

const auditsStore = useAuditsStore()

const contextStore = useContext()

const drawerStore = useDrawer()
const { open } = storeToRefs(drawerStore)

if (!props.onWorkflowReview && props.contextId && props.contextType) {
  // Pages except for Workflow Review will be able to discern the context readily on mount
  contextStore.setContext(props.contextType, props.contextId)

  // Eagerly load the changes since last visit to display the appropriate unread count UI
  auditsStore.fetchChangesSinceLastVisit({
    contextId: props.contextId,
    contextType: props.contextType,
  })
}

/**
 * Handle situations where the right sidepanel is opened but no triggers exist to set the drawer, e.g. Document Viewer
 */
watchEffect(() => {
  if (!parent.value || open.value.direction !== 'initial') {
    return
  }

  const drawer = parent.value.closest('#right-drawer')

  if (drawer && drawer.classList.contains('sf-sidebar__expanded--right')) {
    drawerStore.openRightDrawer(RightDrawerContent.Communication)
  }
})
</script>
